'use strict';

const av = Autodesk.Viewing, avp = av.Private, avu = Autodesk.Viewing.UI;
const namespace = AutodeskNamespace('Autodesk.AEC');
const myExtensionName = 'Autodesk.AEC.CanvasBookmarkExtension';

const LEVEL_EXT = 'Autodesk.AEC.LevelsExtension';

import { createCameraIcon, createClusteredCameraIcon } from './CanvasBookmarkIcons.js';
import { createFrustumShape } from './CreateFrustumShape.js';
import HCluster from './HCluster.js';

const VISIBILITY_PROP = 'canvasBookmarks';

// Name of the overlay scene that we use graphics overlays (frustum shapes)
const OverlayName = 'CanvasBookmark_Extension_Overlay';
const CutPlaneSetName = 'CanvasBookmark_CutPlanes';

// Interpolate between two given Matrix4 transforms. Note that this requires the transforms
// to be invertible combinations of pos/rotate/scale.
const lerpTransforms = (() => {
    let startPos    = new THREE.Vector3();
    let endPos      = new THREE.Vector3();
    let startScale  = new THREE.Vector3();
    let endScale    = new THREE.Vector3();
    let startRotate = new THREE.Quaternion();
    let endRotate   = new THREE.Quaternion();

    let pos    = new THREE.Vector3();
    let scale  = new THREE.Vector3();
    let rotate = new THREE.Quaternion();

    return (startMatrix, endMatrix, t, dstTransform) => {
        startMatrix.decompose(startPos, startRotate, startScale);
        endMatrix.decompose(endPos, endRotate, endScale);

        pos.lerpVectors(startPos, endPos, t);
        scale.lerpVectors(startScale, endScale, t);
        rotate.slerpQuaternions(startRotate, endRotate, t);

        dstTransform.compose(pos, rotate, scale);

        return dstTransform;
    };
})();

// Interpolates between two bboxes. t is in [0,1]
const lerpBoxes = (startBox, endBox, t, dstBox) => {
    dstBox.min.lerpVectors(startBox.min, endBox.min, t);
    dstBox.max.lerpVectors(startBox.max, endBox.max, t);
    lerpTransforms(startBox.transform, endBox.transform, t, dstBox.transform);
};

// Clone Box3 with attached transform
const cloneBox = (srcBox)=> {
    let box = srcBox.clone();
    box.transform = srcBox.transform ? srcBox.transform.clone() : new THREE.Matrix4();
    return box;
};

const setButtonVisible = (button, value) => {

    // Make it detectable if this button is going to change on next anim frame.
    button.visiblityChangePending = true;

    // Delay class change to next anim frame - otherwise css transitions don't work sometimes.
    requestAnimationFrame(() => {
        if (value) {
            button.container.classList.add('visible');
        }
        else {
            button.container.classList.remove('visible');
        }
        button.visiblityChangePending = false;
    });
};


export default class CanvasBookmarkExtension extends av.Extension {
    constructor(viewer, options) {
        super(viewer, options);

        this.viewer = viewer;

        this.bookmarks = [];
        this.clusteredBookmarks = [];

        this.camera = this.viewer.navigation.getCamera();
        this.cameraMovedCB = this.onCameraMoved.bind(this);
        this.onModelTransformChanged = this.onModelTransformChanged.bind(this);
        this.onVisibilityChangedCB = this.onVisibilityChanged.bind(this);

        // SectionBox animations
        this.sectionBox          = null; // SectionBox as defined by currently hovered bookmark (not changed during anims)
        this.activeSectionBox    = null; // Latest applied SectionBox
        this.animStartSectionBox = null; // Used only during cutplane animations
        this.animEndSectionBox   = null; // Usually equal to this.sectionBox, but this.sectionBox may be null while animEndSectionBox is always valid during an animation
        this.sectionBoxAnim      = null; // AnimControl - only existing during animations

        // bookmarks visibility logic
        this.changeBookmarksVisibility = this.changeBookmarksVisibility.bind(this);
        this.changeBookmarksVisibilityTimeout = null;

        this._clusteringEnabled = this.options.clusterfck !== undefined;
        this._clusteringThreshold = this.options.clusteringThreshold || 110; // threshold is (icon_width * 5), depends on "THREE.Vector3.distanceTo()"

        // bookmarks clustering
        if (this._clusteringEnabled) {
            this.hcluster = new HCluster(
                this.options.clusterfck,
                (b1, b2) => {
                    const _b1 = new THREE.Vector2(b1.currentPosProjected.x, b1.currentPosProjected.y);
                    const _b2 = new THREE.Vector2(b2.currentPosProjected.x, b2.currentPosProjected.y);
                    return _b2.distanceTo(_b1);
                },
                this._clusteringThreshold
            );
        }

        this._filterOutInvisibleBookmark = this._filterOutInvisibleBookmark.bind(this);

        this.levelChangedCB = this.onLevelChanged.bind(this);

        this.extensionLoadedCB = this.onExtensionLoaded.bind(this);

        // Indicates if any frustumShape is currently fading for hover effects.
        this.isFrustumFading = false;

        // For testing: Callbacks that will be notified once as soon as Minimap has reached stable state
        this.waitingCallbacks = [];
    }

    setBookmarksVisible(visible) {
        this.bookmarks.forEach(b => setButtonVisible(b.marker, visible));
        this.clusteredBookmarks.forEach(b => setButtonVisible(b.marker, visible));
        if (!visible) {
            this.stopGhosting();
        }
    }

    onVisibilityChanged(visible) {
        if (this.viewer.getPreferences()) {
            this.viewer.getPreferences().set(VISIBILITY_PROP, visible);
        }

        this.setBookmarksVisible(visible);

        if (visible) {
            this.updateBookmarkPositions();
        }
    }

    isVisible() {
        return this.viewer.getPreferences() && this.viewer.getPreferences().get(VISIBILITY_PROP);
    }

    onCameraMoved() {
        this.setBookmarksVisible(false);
        this.debounceBookmarksVisibility();
    }

    createTooltip(button, geomNode) {
        const name = geomNode.name();
        button.setToolTip(name);
        // Replace tooltip content with bold text
        const b = button._toolTipElement.ownerDocument.createElement('b');
        b.textContent = name;
        button._toolTipElement.replaceChildren(b);
    }

    flyToBookmark(bookmark) {
        const dstView = {
            position: bookmark.pos,
            target:   bookmark.target,
            up:       bookmark.up
        };
        this.anim = avp.flyToView(this.viewer, dstView, 1.0);
    }

    getRelatedModelTransform(geomNode) {
        const root = geomNode.getRootNode();

        // Find the current visible model that share the same root node with the bookmark's node.
        const relatedModel = this.viewer.getVisibleModels().find(
            model => model.getDocumentNode().getRootNode() === root
        );

        const modelTransform = relatedModel?.getModelToViewerTransform();

        return modelTransform;
    }

    // geomNode is a BubbleNode of type GEOMETRY_NODE
    createBookmark(geomNode) {

        // extract camera data (TODO: Add generic utility function to BubbleNode to read camera)
        const camera = geomNode.extractCamera();
        if (!camera) {
            return;
        }

        let pos    = camera.position;
        let target = camera.target;

        const modelTransform = this.getRelatedModelTransform(geomNode);

        // Transform pos & target according to the modelToViewerTransform of the related model.
        if (modelTransform) {
            pos.applyMatrix4(modelTransform);
            target.applyMatrix4(modelTransform);
        }

        const dir = new THREE.Vector3(target.x - pos.x, target.y - pos.y, target.z - pos.z).normalize();
        const up  = new THREE.Vector3(0, 0, 1);

        // IDs cannot start with numbers and have whitespaces
        let button = new Autodesk.Viewing.UI.Button(`b-${geomNode.name().replace(/\s/g,'')}`);

        // Configure style (TODO: Should move to css class or specified by client app)
        button.container.classList.add('canvas-bookmark');
        button.container.innerHTML = createCameraIcon();
        setButtonVisible(button, this.isVisible());

        this.createTooltip(button, geomNode);

        this.viewer.container.appendChild(button.container);

        const buttonSize = button.getDimensions();

        const sectionBox = this.findSectionBox(geomNode, geomNode.name());
        const sectionBoxCenter = sectionBox ? sectionBox.getCenter(new THREE.Vector3()).applyMatrix4(sectionBox.transform) : null;

        const bookmark = {
            marker: button,
            pos: sectionBox ? sectionBoxCenter : pos,
            cameraPos : pos,
            dir: dir,
            up: up,
            target: target,
            name: geomNode.name(),

            // position within the marker that should be centered
            anchorX: buttonSize.width  / 2,
            anchorY: buttonSize.height / 2,

            fadeValue: 0.0,
            hovered: false,
            bubbleNode: geomNode,

            clustered: false
        };

        button.container.addEventListener('mouseenter', () => { this.onBookmarkMouseEnter(bookmark); });
        button.container.addEventListener('mouseleave', () => { this.onBookmarkMouseLeave(bookmark); });

        button.addEventListener(avu.Button.Event.CLICK, () => {
            // pass to custom handler
            if (this.options && this.options.onBookmark) {
                this.options.onBookmark(bookmark.bubbleNode, camera);
            }
        });

        return bookmark;
    }

    createClusteredBookmark(flattenCluster, id, visible) {
        let button = new Autodesk.Viewing.UI.Button(id);

        button.container.classList.add('clustered-canvas-bookmark');
        button.container.innerHTML = createClusteredCameraIcon();

        setButtonVisible(button, this.isVisible() && visible);

        this.viewer.container.appendChild(button.container);

        const res = HCluster.ComputeMeanValueForFlattenCluster(flattenCluster);

        const bookmark = {
            marker: button,
            name: id,

            pos: res.center3D,
            currentPosProjected: res.center2D,

            cluster: flattenCluster,

            fadeValue: 0.0,
            hovered: false,

            folded: true
        };

        button.container.addEventListener('mouseenter', () => { this.onClusteredBookmarkMouseEnter(bookmark); });
        button.container.addEventListener('mouseleave', () => { this.onClusteredBookmarkMouseLeave(bookmark); });
        button.addEventListener(avu.Button.Event.CLICK, () => { this.onClusteredBookmarkClicked(bookmark);});

        bookmark.marker.container.style.transform = `translate(${res.center2D.x}px, ${res.center2D.y}px)`;
        this.clusteredBookmarks.push(bookmark);
    }

    deleteBookmark(bookmark) {
        this.viewer.container.removeChild(bookmark.marker.container);
        this.hideFrustumShape(bookmark);
    }

    clearBookmarks() {
        // Cancel any outdated anims or cutplanes
        this.stopSectionBoxAnim();
        this.applySectionBox();

        this.bookmarks.forEach(bm => {
            this.deleteBookmark(bm);
        });
        this.bookmarks = [];

        this.clusteredBookmarks.forEach(bm => {
            this.deleteBookmark(bm);
        });
        this.clusteredBookmarks = [];
    }

    resetBookmarks(geomNodes) {

        this.clearBookmarks();
        if (!geomNodes) {
            return;
        }

        //The bookmarks make use of AECModelData, so we have to make sure we have that loaded.
        av.Document.getAecModelData(geomNodes).then( () => {

            for (let i = 0; i < geomNodes.length; i++) {
                const geomNode = geomNodes[i];
                const bookmark = this.createBookmark(geomNode);
                if (bookmark) {
                    this.bookmarks.push(bookmark);
                }
            }

            this.bookmarkData = geomNodes;

            this.updateBookmarkPositions();
        });
    }

    // Create bookmarks with correct positions for the current geoOffset
    resetGlobalOffset(globalOffset) {
        this.options.globalOffset = globalOffset;
        this.resetBookmarks(this.bookmarkData);
    }

    showFrustumShape(bookmark) {
        if (bookmark.frustumShape) {
            return;
        }

        bookmark.frustumShape = createFrustumShape(bookmark.cameraPos, bookmark.dir);
        this.viewer.impl.addOverlay(OverlayName, bookmark.frustumShape);

        bookmark.frustumShape.name = bookmark.bubbleNode.name();

        // Make LMV recompute near/far plane, so that the frustum shape is considered
        // TODO: It would be easier if LMV would do that automatically, but it
        //       updates the camera only when rendering the scene.
        //       => Clarify if we can generally change that.
        this.viewer.impl.updateCameraMatrices();
    }

    hideFrustumShape(bookmark) {
        if (!bookmark.frustumShape) {
            return;
        }

        this.viewer.impl.removeOverlay(OverlayName, bookmark.frustumShape);
        bookmark.frustumShape = null;
    }

    // Controls the frustum fading. fadeValue is in [0,1]
    setFrustumFadeValue(bookmark, fadeValue) {
        bookmark.fadeValue = fadeValue;
        if (bookmark.frustumShape) {
            bookmark.frustumShape.setFadeValue(fadeValue);
        }
    }

    // Update fading of the frustum shape based on elpased time in ms.
    updateFrustumShapeFading(bookmark, elapsed) {
        const FadeInDurationMs  = 500;
        const FadeOutDurationMs = 500;

        const fadingIn  = (bookmark.hovered  && bookmark.fadeValue < 1.0);
        const fadingOut = (!bookmark.hovered && bookmark.fadeValue > 0.0);

        if (!fadingIn && !fadingOut) {
            return false;
        }

        // compute amount of fading change (1.0 = full fade)
        let fadeInDelta  = elapsed / FadeInDurationMs;
        let fadeOutDelta = elapsed / FadeOutDurationMs;
        let newValue = bookmark.fadeValue + (fadingIn ? fadeInDelta : -fadeOutDelta);

        newValue = THREE.Math.clamp(newValue, 0, 1);
        this.setFrustumFadeValue(bookmark, newValue);

        // HACK: Use css fade instead
        if (bookmark.marker && bookmark.marker._toolTipElement) {
            bookmark.marker._toolTipElement.style.opacity = Math.min(1, 3 * newValue);
        }

        // remove frustums if not used anymore
        if (!bookmark.hovered && bookmark.fadeValue === 0) {
            this.hideFrustumShape(bookmark);
        }

        this.viewer.impl.invalidate(false, false, true);

        return true;
    }

    requestTimer() {
        const _window = this.getWindow();
        this.timerId = _window.requestAnimationFrame(() => this.onTimer());
    }

    onTimer() {
        // get elapsed time in ms
        const curTime = performance.now();
        const elapsed = curTime - this.lastTime;
        this.lastTime = curTime;

        // Track whether any frustum-shape fading is going on
        this.isFrustumFading = false;

        this.bookmarks.forEach(bookmark => {
            const fading = this.updateFrustumShapeFading(bookmark, elapsed);
            this.isFrustumFading = this.isFrustumFading || fading;
        });

        // Trigger notification if fading stuff is finished
        if (this.isAllDone()) {
            this.waitingCallbacks.forEach(p => p());
            this.waitingCallbacks.length = 0;
        }

        this.requestTimer();
    }

    // Returns an oriented box (THREE.Box3() + attached Matrix4 box.transform)
    findSectionBox(bubbleNode, name) {
        const viewNode = bubbleNode.search({ type: 'view' });

        if (!viewNode.length || viewNode[0].data.name !== name)
            return null;

        const sb = viewNode[0].data.sectionBox;
        const sbt = viewNode[0].data.sectionBoxTransform;

        if (!sb || !sbt)
            return null;

        const sbTransformMatrix = new THREE.Matrix4().fromArray([
            sbt[0], sbt[1],   sbt[2],   sbt[3],
            sbt[4], sbt[5],   sbt[6],   sbt[7],
            sbt[8], sbt[9],   sbt[10],  sbt[11],
            sbt[12], sbt[13], sbt[14],  sbt[15]
        ]);

        let box = new THREE.Box3();
        box.min.set(sb[0], sb[1], sb[2]);
        box.max.set(sb[3], sb[4], sb[5]);
        box.transform = new THREE.Matrix4();

        // Transform box according to the modelToViewerTransform of the related model.
        // Note: The model that the bookmark refers to is not always in memory. However,
        //   - The transform is always the same for all 3D views of a model.
        //   - Since a CanvasBookmark is shown, at least some view of the same 3D model must be visible
        // Therefore, we can always get the transform here from the visible view of that model.

        const modelTransform = this.getRelatedModelTransform(bubbleNode);

        if (modelTransform) {
            box.transform.multiply(modelTransform);
        }

        box.transform.multiply(sbTransformMatrix);


        return box;
    }

    // The overlay scene contains the frustum shapes and is always visible by default. We only hide it temporarily
    // when baking an image for showin ghost floors
    setOverlaySceneVisible(visible) {
        // Overlay may be null if this is currently unloaded
        let overlay = this.viewer.impl.overlayScenes[OverlayName];
        let scene = overlay && overlay.scene;
        if (scene) {
            scene.visible = visible;
        }
    }

    getFadeExtension() {
        return this.viewer.getExtension('Autodesk.CrossFadeEffects');
    }

    // Render models without SectionBox into fading target
    showGhosting() {
        // Ghosting is an optional effect that requires the CrossFade extension. If we don't have it,
        // we omit ghosting, i.e., only apply the cutplanes for SectionBox previous.
        let fadeExt = this.getFadeExtension();
        if (!fadeExt) {
            return;
        }

        // Take control over CrossFade effect. Purpose is to avoid conflicts with other effects based on CrossFade. E.g., if we are
        // still showing ghost-floors from LevelsExtension, LevelsExtension will properly clean them up first.
        fadeExt.acquireControl('CanvasBookmarks', () => this.stopGhosting());

        // If possible, reuse baked ghosting image from previous bookmark
        if (!this.ghostingActive) {
            this.viewer.impl.setCutPlaneSet(CutPlaneSetName); // disable current SectionBox
            this.setOverlaySceneVisible(false);               // make sure that we don't bake Frustum shapes into the screenshot
            fadeExt.renderFadingImage(0);                     // render snapshot into fading target
            this.applySectionBox(this.activeSectionBox);      // recover current SectionBox
            this.setOverlaySceneVisible(true);                // show overlay scene again (We don't hide it anywhere else, otherwise we would need to save/restore visibility state)
            this.ghostingActive = true;
        } else {
            // Reuse ghosting from before - just make sure that no fadeOut anim is in-progress
            this.stopGhostFadeOut();
        }
        fadeExt.setCrossFadeOpacity(0, 0.3); // overlay full model with ghost opacity
    }

    stopGhosting() {
        if (this.ghostingActive) {
            this.stopGhostFadeOut();
            let fadeExt = this.getFadeExtension();
            fadeExt && fadeExt.releaseFadingImage(0);
            this.ghostingActive = false;
        }
    }

    stopGhostFadeOut() {
        // skip fade-out animation if in progress
        if (this.ghostFadeAnim) {
            this.ghostFadeAnim.skip();
            this.ghostFadeAnim = null;
        }
    }

    // Fade-out cross-fade target containing the ghosted environment
    fadeOutGhosting() {
        let fadeExt = this.getFadeExtension();
        if (!this.ghostingActive || !fadeExt) {
            // No ghosting active => nothing to do
            return;
        }

        let ghostAlpha = fadeExt.getCrossFadeOpacity(0);
        this.ghostFadeAnim = avp.fadeValue(ghostAlpha, 0.0, 0.5, t=>fadeExt.setCrossFadeOpacity(0, t));
    }

    onBookmarkMouseEnter(bookmark) {
        let sectionBox = this.findSectionBox(bookmark.bubbleNode, bookmark.name);
        if (sectionBox) {
            this.showGhosting();
            this.animateToSectionBox(sectionBox);
        }

        this.showFrustumShape(bookmark);
        bookmark.hovered = true;
    }

    onBookmarkMouseLeave(bookmark) {
        this.animateToSectionBox(null);
        this.fadeOutGhosting();
        bookmark.hovered = false;
    }

    onClusteredBookmarkMouseEnter(bookmark) {
        bookmark.hovered = true;
    }

    onClusteredBookmarkMouseLeave(bookmark) {
        bookmark.hovered = false;
    }

    onClusteredBookmarkClicked(bookmark) {
        // TODO:
        //   - Zoom + zoom level computation
        //   - "Too many" clustered bookmarks -> (n+1) icon should be "..."

        if (bookmark.folded) {
            const _rotate = (vector, center, angle) => {
                const c = Math.cos(angle);
                const s = Math.sin(angle);

                const x = vector.x - center.x;
                const y = vector.y - center.y;

                vector.x = x * c - y * s + center.x;
                vector.y = x * s + y * c + center.y;
            };

            const n = bookmark.cluster.length;
            const alpha = Math.round(360.0 / n);

            const center = new THREE.Vector2(bookmark.currentPosProjected.x, bookmark.currentPosProjected.y);

            let start = new THREE.Vector2(center.x, center.y + 35);

            for (let i = 0; i < n; ++i) {
                let bm = bookmark.cluster[i];

                let current = start.clone();
                _rotate(current, center, i * alpha * Math.PI / 180.0);

                bm.currentPosProjected.x = current.x;
                bm.currentPosProjected.y = current.y;

                bm.marker.container.style.transform = `translate(${current.x}px, ${current.y}px)`;
                setButtonVisible(bm.marker, true);
            }

            bookmark.marker.container.classList.add('declustered-canvas-bookmark');
        }
        else {
            bookmark.cluster.forEach(bm => {
                setButtonVisible(bm.marker, false);
            });

            bookmark.marker.container.classList.remove('declustered-canvas-bookmark');
        }

        bookmark.folded = !bookmark.folded;
    }

    debounceBookmarksVisibility() {
        if (this.changeBookmarksVisibilityTimeout) {
            clearTimeout(this.changeBookmarksVisibilityTimeout);
        }

        this.changeBookmarksVisibilityTimeout = setTimeout(this.changeBookmarksVisibility, 100);
    }

    debounceResetBookmarks() {
        if (this.resetBookmarksTimeout) {
            clearTimeout(this.resetBookmarksTimeout);
        }

        this.resetBookmarksTimeout = setTimeout(() => this.resetBookmarks(this.bookmarkData), 100);
    }

    onModelTransformChanged() {
        this.onCameraMoved();
        this.debounceResetBookmarks();
    }

    changeBookmarksVisibility() {
        this.updateBookmarkPositions();
    }

    updateBookmarkPositions() {
        if (!this.isVisible()) {
            return;
        }

        this.bookmarks.forEach(bookmark => {
            // Use cropBox center (if available) or camera position to choose label position
            const sBox  = bookmark.bubbleNode.sectionBox;
            const pos3D = sBox ? sBox.getCenter(new THREE.Vector3()) : bookmark.pos;

            bookmark.currentPos3D = pos3D;

            // In-canvas coordinates
            let pos2D = this.project(pos3D);
            pos2D.x -= bookmark.anchorX;
            pos2D.y -= bookmark.anchorY;

            bookmark.currentPosProjected = new THREE.Vector3(pos2D.x, pos2D.y, pos2D.z);
            bookmark.marker.container.style.transform = `translate(${pos2D.x}px, ${pos2D.y}px)`;
        });

        const clusters = this._clusteringEnabled ? this.hcluster.createClusters(this.bookmarks) : [];
        this.updateClusteredBookmarks(clusters);
    }

    updateClusteredBookmarks(clusters) {
        // delete all "cluster" bookmarks since we create them all the time (by design), hiding will not really work
        this.clusteredBookmarks.forEach(bookmark => {
            this.deleteBookmark(bookmark);
        });
        this.clusteredBookmarks = [];

        // hide all bookmarks and reset "clustered" state
        this.bookmarks.forEach(bookmark => {
            setButtonVisible(bookmark.marker, false);
        });

        const floorSelector = this._getFloorSelector();
        const currentLevel = floorSelector && floorSelector.currentFloor;
        if (currentLevel !== undefined) {
            return;
        }

        const visibleBookamrks = this.bookmarks.filter(this._filterOutInvisibleBookmark);
        visibleBookamrks.forEach(bookmark => {
            bookmark.clustered = false;
            setButtonVisible(bookmark.marker, true);
        });

        for (let i = 0; i < clusters.length; i++) {
            const c = clusters[i];

            if (c.size > 1) {
                const flCluster = HCluster.FlattenCluster(c);

                let clusterBmVisible = false;
                flCluster.forEach(bookmark => {
                    bookmark.clustered = true;
                    if (!clusterBmVisible && bookmark.marker.isVisible()) {
                        // if one of the included bookmarks is visible, mark the cluster bookmark as visible one
                        clusterBmVisible = true;
                    }
                    // now, mark the included bookmark as invisible one
                    setButtonVisible(bookmark.marker, false);
                });

                // IDs cannot start with some number
                this.createClusteredBookmark(flCluster, `c-${flCluster[0].bubbleNode.data.viewableID}`, clusterBmVisible);
            }
        }
    }

    project(position) {
        const containerBounds = this.viewer.navigation.getScreenViewport();
        let p = new THREE.Vector3().copy(position);

        const matrix = new THREE.Matrix4();
        matrix.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);

        p.applyProjection(matrix);

        return new THREE.Vector3(
            Math.round(((p.x + 1) / 2) * containerBounds.width),
            Math.round(((-p.y + 1) / 2) * containerBounds.height),
            p.z
        );
    }

    load() {

        this.viewer.getPreferences().add(VISIBILITY_PROP, true);

        this.viewer.addEventListener(av.CAMERA_CHANGE_EVENT, this.cameraMovedCB);
        this.viewer.addEventListener(av.MODEL_TRANSFORM_CHANGED_EVENT, this.onModelTransformChanged);

        const floorSelector = this._getFloorSelector();
        if (floorSelector) {
            floorSelector.addEventListener(Autodesk.AEC.FloorSelector.SELECTED_FLOOR_CHANGED, this.levelChangedCB);
        }

        this.viewer.addEventListener(av.EXTENSION_LOADED_EVENT, this.extensionLoadedCB);

        // handle the case when this extension is loaded after LevelsExtension is loaded,
        // without making this extension known to LevelsExtension
        this.updateBookmarkPositions();

        this.viewer.impl.createOverlayScene(OverlayName);

        // trigger timer-calls for fading animations
        this.lastTime = performance.now();
        this.requestTimer();

        if (!this.updateSettingsPanel) {
            //We need to connect this to an event that happens after the settings panel has been
            //create by the load sequence.
            this.updateSettingsPanel = this.configureSettings.bind(this);
            this.viewer.addEventListener(av.SETTINGS_PANEL_CREATED_EVENT, this.updateSettingsPanel);
        }

        return true;
    }

    unload() {
        if (this.resetBookmarksTimeout) {
            clearTimeout(this.resetBookmarksTimeout);
        }
        this.clearBookmarks();

        this.viewer.impl.removeOverlayScene(OverlayName);

        this.viewer.removeEventListener(av.CAMERA_CHANGE_EVENT, this.cameraMovedCB);
        this.viewer.removeEventListener(av.MODEL_TRANSFORM_CHANGED_EVENT, this.onModelTransformChanged);

        const floorSelector = this._getFloorSelector();
        if (floorSelector) {
            floorSelector.removeEventListener(Autodesk.AEC.FloorSelector.SELECTED_FLOOR_CHANGED, this.levelChangedCB);
        }

        this.viewer.removeEventListener(av.EXTENSION_LOADED_EVENT, this.extensionLoadedCB);

        // Stop timer for position updates
        if (this.timerId) {
            const _window = this.getWindow();
            _window.cancelAnimationFrame(this.timerId);
            this.timerId = null;
        }

        if (this.updateSettingsPanel) {
            this.viewer.removeEventListener(av.SETTINGS_PANEL_CREATED_EVENT, this.updateSettingsPanel);
            this.updateSettingsPanel = null;
        }

        return true;
    }

    applySectionBox(box) {
        this.activeSectionBox = box;

        // No box => clear cutplanes
        if (!box) {
            this.viewer.impl.setCutPlaneSet(CutPlaneSetName);
            return;
        }

        const planes = avp.SceneMath.box2CutPlanes(box, box.transform);
        this.viewer.impl.setCutPlaneSet(CutPlaneSetName, planes);
    }

    // get bbox of all visible models
    getModelBox() {
        const mq = this.viewer.impl.modelQueue();
        const models = mq.getModels();

        let box = new THREE.Box3();
        for (let i=0; i<models.length; i++) {
            box.union(models[i].getBoundingBox());
        }

        return box;
    }

    stopSectionBoxAnim() {
        if (this.sectionBoxAnim) {
            this.sectionBoxAnim.stop();
            this.sectionBoxAnim = null;
        }
    }

    animateToSectionBox(newBox) {
        // Nothing to animate
        if (!newBox && !this.activeSectionBox) {
            return;
        }

        // Don't allow overlapping animations
        this.stopSectionBoxAnim();

        const CutPlaneAnimInSeconds = 0.5;

        // Start at latest SectionBox state or with the whole model
        this.animStartBox = this.activeSectionBox ? cloneBox(this.activeSectionBox) : cloneBox(this.getModelBox());
        this.animEndBox   = newBox ? cloneBox(newBox) : cloneBox(this.getModelBox());

        // Set target box
        this.sectionBox = newBox;

        // interpolated box used during anim
        let tmpBox = new THREE.Box3();
        tmpBox.transform = new THREE.Matrix4();

        const onUpdate = (t) => {
            t = avp.smootherStep(t);
            lerpBoxes(this.animStartBox, this.animEndBox, t, tmpBox);
            this.applySectionBox(tmpBox);
        };

        const onFinished = () => {
            this.applySectionBox(this.sectionBox);
            this.sectionBoxAnim = null;
        };

        this.sectionBoxAnim = avp.fadeValue(0, 1, CutPlaneAnimInSeconds, onUpdate, onFinished);
    }

    enableClustering(value) {
        if (this._clusteringEnabled !== value) {
            this._clusteringEnabled = value;
            this.updateClusteredBookmarks([]);
        }
    }

    setClusteringThreshold(value) {
        if (this._clusteringThreshold !== value) {
            this._clusteringThreshold = value;
            this.hcluster.setThreshold(value);
            this.updateClusteredBookmarks([]);
        }
    }

    // Returns true if there are no active transitions, i.e., state is stable and doesn't vary based on timing anymore.
    isAllDone() {
        // Note: Some aspects are not considered yet, e.g., css-based fading and debounced visibility. For
        //       now, we only address everything that would introduce test instabilities otherwise.
        const sectionBoxAnimActive = this.sectionBoxAnim && this.sectionBoxAnim.isRunning;
        const visChangePending = this.bookmarks.some(b => b.marker && b.marker.visiblityChangePending);
        return !sectionBoxAnimActive && !this.isFrustumFading && !visChangePending;
    }

    // Returns a promise that resolves as soon as isAllDone() returns true.
    waitForAllDone() {

        // Update frustum-fading to check whether there are any changes to process
        this.onTimer();

        if (this.isAllDone()) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            this.waitingCallbacks.push(resolve);
        });
    }

    _filterOutInvisibleBookmark(bm) {
        const vp = this.viewer.navigation.getScreenViewport();

        if (bm.currentPosProjected.x < 0 || bm.currentPosProjected.x > vp.width) {
            return false;
        }

        if (bm.currentPosProjected.y < 0 || bm.currentPosProjected.y > vp.height) {
            return false;
        }

        const viewingDirection = this.camera.target.clone().sub(this.camera.position);
        const bookmarkDirection = bm.pos.clone().sub(this.camera.position);
        return viewingDirection.dot(bookmarkDirection) > 0;
    }

    onLevelChanged(/*event*/) {
        this.updateBookmarkPositions();
    }

    onExtensionLoaded(event) {
        if (event.extensionId === LEVEL_EXT) {
            this.updateBookmarkPositions();
        }
    }

    configureSettings() {

        //Is it a GUI viewer or not?
        if (!this.viewer.getSettingsPanel)
            return;

        let panel = this.viewer.getSettingsPanel(true);

        if (!panel.is3dMode)
            return;

        let navTab = av.Extensions.ViewerSettingTab.Navigation;
        panel.addLabel(navTab, "Location Icons");

        // add toggle to the hypermodeling tab of the viewer settings
        if (panel && !panel.showLocationIconsChkBoxId) {
            panel.showLocationIconsChkBoxId = panel.addCheckbox(
                navTab,
                "Show location icons",
                "Toggles availability of the location icons when looking at the project model",
                true,
                this.onVisibilityChangedCB,
                VISIBILITY_PROP
            );
        }
    }

    _getFloorSelector() {
        const levelExt = this.viewer.getExtension(LEVEL_EXT);
        return levelExt && levelExt.floorSelector;
    }
}

namespace.CanvasBookmarkExtension = CanvasBookmarkExtension;

Autodesk.Viewing.theExtensionManager.registerExtension(myExtensionName, CanvasBookmarkExtension);
